import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    TextField,
    InputAdornment,
    Pagination,
    Skeleton,
    Alert,
    Snackbar,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Breadcrumbs,
    Link,
    CircularProgress,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import GroupIcon from '@mui/icons-material/Group'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import AdminGuard from './AdminGuard'
import {
    useAdminOrganizationDetailQuery,
    useAdminUsersByOrgQuery,
    useAdminRoadmapsByOrgQuery,
    useUpdateOrganizationFlagsMutation,
    useUpdateUserRolesMutation,
    useExportOrgEventsMutation,
    AdminUser,
    AdminRoadmap,
} from '../../shared/queries/admin'
import AllowedUsersDialog from './components/AllowedUsersDialog'
import AddUsersDialog from './components/AddUsersDialog'
import Tabs from '../../shared/components/Tabs/Tabs'

const AdminOrganizationDetail: React.FC = () => {
    const { orgId } = useParams<{ orgId: string }>()
    const history = useHistory()
    const [selectedTabId, setSelectedTabId] = useState('users')
    const [userPage, setUserPage] = useState(1)
    const [roadmapPage, setRoadmapPage] = useState(1)
    const [userSearch, setUserSearch] = useState('')
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    })
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean
        title: string
        message: string
        onConfirm: () => void
    }>({ open: false, title: '', message: '', onConfirm: () => {} })
    const [selectedRoadmapId, setSelectedRoadmapId] = useState<string | null>(null)
    const [addUsersOpen, setAddUsersOpen] = useState(false)
    const [exportStartDate, setExportStartDate] = useState('')
    const [exportEndDate, setExportEndDate] = useState('')

    const { data: orgData, isLoading: orgLoading } = useAdminOrganizationDetailQuery(orgId)
    const { data: usersData, isLoading: usersLoading } = useAdminUsersByOrgQuery(orgId, userPage, 20, userSearch)
    const { data: roadmapsData, isLoading: roadmapsLoading } = useAdminRoadmapsByOrgQuery(orgId, roadmapPage, 20)

    const updateFlagsMutation = useUpdateOrganizationFlagsMutation()
    const updateUserRolesMutation = useUpdateUserRolesMutation()
    const exportMutation = useExportOrgEventsMutation()

    const organization = orgData
    const users = usersData?.data?.users || []
    const usersPagination = usersData?.data?.pagination
    const roadmaps = roadmapsData?.data?.roadmaps || []
    const roadmapsPagination = roadmapsData?.data?.pagination

    const handleFlagToggle = (flag: string, currentValue: boolean) => {
        setConfirmDialog({
            open: true,
            title: `${currentValue ? 'Disable' : 'Enable'} ${flag.replace('is', '').replace('Enabled', '')}?`,
            message: `Are you sure you want to ${currentValue ? 'disable' : 'enable'} this feature for ${organization?.name}?`,
            onConfirm: async () => {
                try {
                    await updateFlagsMutation.mutateAsync({
                        orgId,
                        flags: { [flag]: !currentValue },
                    })
                    setSnackbar({ open: true, message: 'Feature flag updated successfully', severity: 'success' })
                } catch (e) {
                    setSnackbar({ open: true, message: 'Failed to update feature flag', severity: 'error' })
                }
                setConfirmDialog((prev) => ({ ...prev, open: false }))
            },
        })
    }

    const handleUserRoleToggle = (user: AdminUser, field: 'readonly' | 'commentingReadonly') => {
        const currentValue = user[field] || false
        setConfirmDialog({
            open: true,
            title: `${currentValue ? 'Remove' : 'Add'} ${field === 'readonly' ? 'read-only' : 'commenting read-only'} access?`,
            message: `Are you sure you want to ${currentValue ? 'remove' : 'add'} ${field === 'readonly' ? 'read-only' : 'commenting read-only'} access for ${user.name}?`,
            onConfirm: async () => {
                try {
                    await updateUserRolesMutation.mutateAsync({
                        userId: user._id,
                        roles: { [field]: !currentValue },
                    })
                    setSnackbar({ open: true, message: 'User role updated successfully', severity: 'success' })
                } catch (e) {
                    setSnackbar({ open: true, message: 'Failed to update user role', severity: 'error' })
                }
                setConfirmDialog((prev) => ({ ...prev, open: false }))
            },
        })
    }

    const usersTabContent = (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search users by name or email..."
                    value={userSearch}
                    onChange={(e) => {
                        setUserSearch(e.target.value)
                        setUserPage(1)
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 300 }}
                />
                <Button
                    variant="contained"
                    disableRipple
                    startIcon={<PersonAddIcon />}
                    style={{ height: 52, borderRadius: '14px' }}
                    onClick={() => setAddUsersOpen(true)}
                >
                    Add Users
                </Button>
            </Box>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'figma.grayscale.stroke' }}
            >
                <Table>
                    <TableHead sx={{ bgcolor: 'figma.grayscale.table2' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}>
                                Name
                            </TableCell>
                            <TableCell sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}>
                                Email
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}
                            >
                                Read-only
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}
                            >
                                Commenting Read-only
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {usersLoading
                            ? Array.from({ length: 5 }).map((_, i) => (
                                  <TableRow key={i}>
                                      <TableCell>
                                          <Skeleton />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton />
                                      </TableCell>
                                  </TableRow>
                              ))
                            : users.map((user: AdminUser) => (
                                  <TableRow
                                      key={user._id}
                                      sx={{ '&:hover': { bgcolor: 'figma.grayscale.frameBackground' } }}
                                  >
                                      <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {user.name}
                                              {user.isAdmin && (
                                                  <Chip
                                                      label="Admin"
                                                      size="small"
                                                      sx={{
                                                          bgcolor: 'figma.primary2',
                                                          color: 'figma.primary',
                                                          fontWeight: 500,
                                                      }}
                                                  />
                                              )}
                                          </Box>
                                      </TableCell>
                                      <TableCell>{user.email}</TableCell>
                                      <TableCell align="center">
                                          <Switch
                                              size="small"
                                              checked={user.readonly || false}
                                              onChange={() => handleUserRoleToggle(user, 'readonly')}
                                          />
                                      </TableCell>
                                      <TableCell align="center">
                                          <Switch
                                              size="small"
                                              checked={user.commentingReadonly || false}
                                              onChange={() => handleUserRoleToggle(user, 'commentingReadonly')}
                                          />
                                      </TableCell>
                                  </TableRow>
                              ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {usersPagination && usersPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={usersPagination.totalPages}
                        page={userPage}
                        onChange={(_, value) => setUserPage(value)}
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    )

    const roadmapsTabContent = (
        <Box>
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'figma.grayscale.stroke' }}
            >
                <Table>
                    <TableHead sx={{ bgcolor: 'figma.grayscale.table2' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}>
                                Product
                            </TableCell>
                            <TableCell sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}>
                                Indication
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}
                            >
                                Status
                            </TableCell>
                            {organization?.isAllowedUsersEnabled && (
                                <>
                                    <TableCell
                                        align="center"
                                        sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}
                                    >
                                        Allowed Users
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{ color: 'figma.grayscale.textHier2', fontWeight: 600, fontSize: 12 }}
                                    >
                                        Actions
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roadmapsLoading
                            ? Array.from({ length: 5 }).map((_, i) => (
                                  <TableRow key={i}>
                                      <TableCell>
                                          <Skeleton />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton />
                                      </TableCell>
                                      {organization?.isAllowedUsersEnabled && (
                                          <>
                                              <TableCell>
                                                  <Skeleton />
                                              </TableCell>
                                              <TableCell>
                                                  <Skeleton />
                                              </TableCell>
                                          </>
                                      )}
                                  </TableRow>
                              ))
                            : roadmaps.map((roadmap: AdminRoadmap) => (
                                  <TableRow
                                      key={roadmap._id}
                                      sx={{ '&:hover': { bgcolor: 'figma.grayscale.frameBackground' } }}
                                  >
                                      <TableCell>{roadmap.product}</TableCell>
                                      <TableCell>{roadmap.indication}</TableCell>
                                      <TableCell align="center">
                                          <Chip
                                              label={roadmap.isActive !== false ? 'Active' : 'Inactive'}
                                              color={roadmap.isActive !== false ? 'success' : 'default'}
                                              size="small"
                                          />
                                      </TableCell>
                                      {organization?.isAllowedUsersEnabled && (
                                          <>
                                              <TableCell align="center">
                                                  <Chip
                                                      label={`${roadmap.allowedUsersCount} users`}
                                                      size="small"
                                                      variant="outlined"
                                                  />
                                              </TableCell>
                                              <TableCell align="center">
                                                  <IconButton
                                                      size="small"
                                                      disableRipple
                                                      onClick={() => setSelectedRoadmapId(roadmap._id)}
                                                      title="Manage allowed users"
                                                  >
                                                      <GroupIcon />
                                                  </IconButton>
                                              </TableCell>
                                          </>
                                      )}
                                  </TableRow>
                              ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {roadmapsPagination && roadmapsPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={roadmapsPagination.totalPages}
                        page={roadmapPage}
                        onChange={(_, value) => setRoadmapPage(value)}
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    )

    const flagsTabContent = orgLoading ? (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'figma.grayscale.stroke' }}>
            <CardContent>
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 2 }} />
                ))}
            </CardContent>
        </Card>
    ) : (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'figma.grayscale.stroke' }}>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Organization Features
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={organization?.isPicoEnabled || false}
                                onChange={() => handleFlagToggle('isPicoEnabled', organization?.isPicoEnabled || false)}
                            />
                        }
                        label="PICO Enabled"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={organization?.isMaiaEnabled || false}
                                onChange={() => handleFlagToggle('isMaiaEnabled', organization?.isMaiaEnabled || false)}
                            />
                        }
                        label="MAIA Enabled"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={organization?.isAllowedUsersEnabled || false}
                                onChange={() =>
                                    handleFlagToggle(
                                        'isAllowedUsersEnabled',
                                        organization?.isAllowedUsersEnabled || false
                                    )
                                }
                            />
                        }
                        label="Roadmap Access Control Enabled"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={organization?.isFreeTrial || false}
                                onChange={() => handleFlagToggle('isFreeTrial', organization?.isFreeTrial || false)}
                            />
                        }
                        label="Free Trial"
                    />
                </Box>
            </CardContent>
        </Card>
    )

    const exportsTabContent = (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'figma.grayscale.stroke' }}>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Activity Dashboard Export
                </Typography>
                <Typography variant="body2" color="figma.grayscale.textHier2" sx={{ mb: 3 }}>
                    Export an HTML activity dashboard for this organization over a selected date range.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Start Date"
                        type="date"
                        size="small"
                        value={exportStartDate}
                        onChange={(e) => setExportStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        size="small"
                        value={exportEndDate}
                        onChange={(e) => setExportEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="contained"
                        disableRipple
                        disabled={exportMutation.isLoading || !exportStartDate || !exportEndDate}
                        startIcon={
                            exportMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : undefined
                        }
                        onClick={async () => {
                            try {
                                const html = await exportMutation.mutateAsync({
                                    orgId,
                                    startDate: exportStartDate,
                                    endDate: exportEndDate,
                                })
                                const blob = new Blob([html], { type: 'text/html' })
                                const url = URL.createObjectURL(blob)
                                const a = document.createElement('a')
                                a.href = url
                                a.download = `activity-${orgId}-${exportStartDate}-${exportEndDate}.html`
                                a.click()
                                URL.revokeObjectURL(url)
                            } catch (err) {
                                setSnackbar({
                                    open: true,
                                    message: (err as any)?.message || 'Export failed',
                                    severity: 'error',
                                })
                            }
                        }}
                    >
                        {exportMutation.isLoading ? 'Exporting…' : 'Export'}
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Last 7 days', days: 7 },
                        { label: 'Last 30 days', days: 30 },
                        { label: 'Last 90 days', days: 90 },
                        { label: 'Last Year', days: 365 },
                    ].map(({ label, days }) => (
                        <Button
                            key={days}
                            size="small"
                            variant="outlined"
                            disableRipple
                            onClick={() => {
                                const end = new Date()
                                const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
                                setExportStartDate(start.toISOString().slice(0, 10))
                                setExportEndDate(end.toISOString().slice(0, 10))
                            }}
                        >
                            {label}
                        </Button>
                    ))}
                </Box>
            </CardContent>
        </Card>
    )

    return (
        <AdminGuard>
            <Box sx={{ p: 3, minHeight: '100vh' }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link
                        component="button"
                        variant="body1"
                        onClick={() => history.push('/admin')}
                        sx={{ cursor: 'pointer' }}
                    >
                        Admin Panel
                    </Link>
                    <Typography color="figma.grayscale.textHier1">{organization?.name || 'Loading...'}</Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <IconButton disableRipple onClick={() => history.push('/admin')}>
                        <ArrowBackIcon />
                    </IconButton>
                    {orgLoading ? (
                        <Skeleton variant="text" width={200} height={40} />
                    ) : (
                        <Typography variant="h4" component="h1">
                            {organization?.name}
                        </Typography>
                    )}
                </Box>

                <Tabs
                    orientation="horizontal"
                    selectedTabId={selectedTabId}
                    onTabChange={setSelectedTabId}
                    tabPanelSx={{ pt: 3 }}
                    tabs={[
                        {
                            id: 'users',
                            label: `Users (${usersPagination?.total || 0})`,
                            view: usersTabContent,
                        },
                        {
                            id: 'roadmaps',
                            label: `Roadmaps (${roadmapsPagination?.total || 0})`,
                            view: roadmapsTabContent,
                        },
                        {
                            id: 'flags',
                            label: 'Feature Flags',
                            view: flagsTabContent,
                        },
                        {
                            id: 'exports',
                            label: 'Exports',
                            view: exportsTabContent,
                        },
                    ]}
                />

                {/* Confirmation Dialog */}
                <Dialog
                    open={confirmDialog.open}
                    onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
                >
                    <DialogTitle>{confirmDialog.title}</DialogTitle>
                    <DialogContent>
                        <Typography>{confirmDialog.message}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button disableRipple onClick={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}>
                            Cancel
                        </Button>
                        <Button disableRipple onClick={confirmDialog.onConfirm} variant="contained" color="primary">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Add Users Dialog */}
                <AddUsersDialog
                    orgId={orgId}
                    open={addUsersOpen}
                    onClose={() => setAddUsersOpen(false)}
                    onSuccess={(count) => {
                        setSnackbar({
                            open: true,
                            message: `${count} user${count !== 1 ? 's' : ''} created successfully`,
                            severity: 'success',
                        })
                    }}
                    onError={(msg) => {
                        setSnackbar({ open: true, message: msg, severity: 'error' })
                    }}
                    isAllowedUsersEnabled={organization?.isAllowedUsersEnabled || false}
                    roadmaps={roadmaps.map((r: AdminRoadmap) => ({
                        _id: r._id,
                        product: r.product,
                        indication: r.indication,
                    }))}
                />

                {/* Allowed Users Dialog */}
                {selectedRoadmapId && (
                    <AllowedUsersDialog
                        roadmapId={selectedRoadmapId}
                        open={!!selectedRoadmapId}
                        onClose={() => setSelectedRoadmapId(null)}
                        onSuccess={() => {
                            setSnackbar({
                                open: true,
                                message: 'Allowed users updated successfully',
                                severity: 'success',
                            })
                        }}
                        onError={() => {
                            setSnackbar({ open: true, message: 'Failed to update allowed users', severity: 'error' })
                        }}
                    />
                )}

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                >
                    <Alert
                        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </AdminGuard>
    )
}

export default AdminOrganizationDetail
